import { UseQueryOptions } from '@tanstack/react-query'
import { z } from 'zod'
import { useApi } from '../../utils/useApi'
import { DetectionSelectorSchema } from './request_images'

// ============================================
// POST /image-request/list_image_requests
// ============================================
// A lot's recent image requests (last ~48h30m, newest first). Split out of get_detections so the
// panel owns its own query and can refresh request statuses without refetching detections.
//
// `status` is a raw backend pass-through string (known values: open | successful |
// partially_successful | failed) — kept as a plain string so an unexpected value never fails the
// whole response parse. `requested_by` is the requester's email (may be null). `detection_selector`
// is the exact same discriminated union the request_images request body uses, so the "apply"
// action can map it straight back onto a get_detections query.

const ImageRequestSchema = z.object({
    request_uuid: z.string(),
    requested_by: z.string().nullable(),
    status: z.string(),
    created_at: z.coerce.date(),
    detection_selector: DetectionSelectorSchema
})
export type ImageRequest = z.infer<typeof ImageRequestSchema>

const ListImageRequestsRequestSchema = z.object({ lot: z.string() })
export type ListImageRequestsRequest = z.infer<typeof ListImageRequestsRequestSchema>

const ListImageRequestsResponseSchema = z.object({
    // Always present (defaults to [] on the backend); `.default([])` guards a malformed payload.
    image_requests: z.array(ImageRequestSchema).default([])
})
export type ListImageRequestsResponse = z.infer<typeof ListImageRequestsResponseSchema>

export { ImageRequestSchema, ListImageRequestsRequestSchema, ListImageRequestsResponseSchema }

// Shared so a freshly-triggered request can invalidate the list (see ActionBarContainer).
export const imageRequestsQueryKey = (lot: string) => ['detectionExplorerImageRequests', lot] as const

/**
 * Lists a lot's recent image requests. Keyed by lot so it refetches on lot change; statuses can be
 * polled by passing a `refetchInterval` through options.
 */
export const useListImageRequestsQuery = (lot: string, options?: { enabled?: boolean; refetchInterval?: number }) => {
    const queryOptions: Omit<UseQueryOptions<ListImageRequestsResponse, Error>, 'queryFn'> = {
        queryKey: imageRequestsQueryKey(lot),
        enabled: options?.enabled,
        refetchInterval: options?.refetchInterval,
        keepPreviousData: true
    }
    return useApi('/image-request/list_image_requests', { lot }, ListImageRequestsResponseSchema, queryOptions)
}
